/**
 * Prepara la foto de un rostro para el terminal facial, en el navegador.
 *
 * El terminal Hikvision rechaza fotos grandes con un error engañoso; se
 * confirmó contra el equipo que ~76 KB entran y 253 KB no. En vez de
 * reescalar en el servidor (que obligaría a una librería de imágenes en el
 * API), se hace aquí: se reduce el lado mayor y se baja la calidad JPEG hasta
 * quedar bajo el objetivo. La cámara del equipo del guardia da fotos de varios
 * MB; esto las deja en unas decenas de KB sin que la cara pierda utilidad.
 */

const LADO_MAX = 600;
const OBJETIVO_BYTES = 150 * 1024;

export async function prepararRostro(archivo: Blob): Promise<Blob> {
  // `from-image` respeta la orientación EXIF: sin esto, una foto tomada de lado
  // con el teléfono llegaría girada y el terminal no encontraría la cara.
  const bitmap = await createImageBitmap(archivo, { imageOrientation: 'from-image' });

  const escala = Math.min(1, LADO_MAX / Math.max(bitmap.width, bitmap.height));
  const ancho = Math.round(bitmap.width * escala);
  const alto = Math.round(bitmap.height * escala);

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;
  const ctx = lienzo.getContext('2d');
  if (!ctx) throw new Error('No se pudo preparar la imagen');
  ctx.drawImage(bitmap, 0, 0, ancho, alto);
  bitmap.close();

  // Se baja la calidad hasta caber. No se persigue el mínimo absoluto: en
  // cuanto entra bajo el objetivo, se conserva la mejor calidad que cupo.
  let calidad = 0.85;
  let blob = await aJpeg(lienzo, calidad);
  while (blob.size > OBJETIVO_BYTES && calidad > 0.4) {
    calidad -= 0.1;
    blob = await aJpeg(lienzo, calidad);
  }
  return blob;
}

function aJpeg(lienzo: HTMLCanvasElement, calidad: number): Promise<Blob> {
  return new Promise((resolver, rechazar) => {
    lienzo.toBlob(
      (blob) => (blob ? resolver(blob) : rechazar(new Error('No se pudo codificar el JPEG'))),
      'image/jpeg',
      calidad,
    );
  });
}
